import { useEffect, useState } from 'react'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { useToast } from '../../components/Toast.jsx'
import { postDepDueAll, postDepDueOne, previewPostDue } from '../../api/operatingCosts'
import { money } from '../sales/salesShared'
import { monthName } from './accountingShared'

// Review what depreciation is due, then post it: one asset (assetId) or
// every active asset (assetId null).
export default function DepreciationReviewModal({ open, assetId = null, onClose, onPosted }) {
  const { notify } = useToast()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [posting, setPosting] = useState(false)

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    setData(null)
    setError('')
    previewPostDue(assetId)
      .then((res) => { if (!cancelled) setData(res) })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not work out what’s due.') })
    return () => { cancelled = true }
  }, [open, assetId])

  const post = async () => {
    setPosting(true)
    try {
      if (assetId) {
        const res = await postDepDueOne(assetId)
        notify(`Posted ${res?.posted || 0} ${res?.posted === 1 ? 'month' : 'months'} of depreciation`)
      } else {
        const res = await postDepDueAll()
        const n = (res?.results || []).reduce((s, r) => s + (r.posted || 0), 0)
        notify(`Posted ${n} ${n === 1 ? 'month' : 'months'} of depreciation`)
      }
      onPosted?.()
      onClose?.()
    } catch (e) {
      setError(e.message || 'Could not post the depreciation.')
    } finally {
      setPosting(false)
    }
  }

  const included = data?.included || []
  const excluded = data?.excluded || []
  return (
    <Modal
      open={open}
      onClose={() => { if (!posting) onClose?.() }}
      title={<>Post depreciation due <HelpTip k="postDue" /></>}
      subtitle="Each month comes out as a journal entry: debit depreciation expense, credit accumulated depreciation."
      preventClose={posting}
      size="lg"
      footer={(
        <>
          <Button variant="ghost" onClick={() => onClose?.()} disabled={posting}>Cancel</Button>
          <Button variant="primary" onClick={post} loading={posting} disabled={!included.length}>{data ? `Post ${money(data.total)}` : 'Post'}</Button>
        </>
      )}
    >
      {error && <p className="acc-modal-error">{error}</p>}
      {!data && !error ? <Spinner label="Working out what’s due…" /> : data && (
        <div className="acc-modal-stack">
          {!included.length && <p className="acc-modal-text">Nothing is due to post.</p>}
          {included.length > 0 && (
            <table className="ci-line-table">
              <thead><tr><th>Asset</th><th>Months</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {included.map((a) => (
                  <tr key={a.asset_id}>
                    <td>{a.name}{a.warning && <span className="cell-sub acc-due">{a.warning}</span>}</td>
                    <td>{(a.periods || []).map((p) => monthName(p.period_ym)).join(', ')}</td>
                    <td className="num">{money(a.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="acc-je-totals"><td colSpan="2">Total</td><td className="num">{money(data.total)}</td></tr></tfoot>
            </table>
          )}
          {excluded.length > 0 && (
            <div>
              <p className="acc-modal-text"><strong>Not posted</strong></p>
              <ul className="acc-check-list">
                {excluded.map((a) => <li key={a.asset_id}>{a.name}: {a.reason}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
